import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import './App.css';

// Question icons mapping
const questionIcons = {
  age: '🎂',
  exercise: '🏃',
  alcohol: '🍺',
  smoking: '🚬',
  animalProtein: '🥩',
  unhealthyDiet: '🍔',
  bmi: '⚖️',
  sleep: '😴',
  stress: '😰',
  heatExposure: '🌡️',
  drugs: '💊',
  toxins: '☠️',
  medical: '🏥',
  sedentary: '💺',
  fruits: '🥗'
};

const ScienceModal = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  
  if (!isOpen) return null;
  
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>×</button>
        <h1>{t('science.title')}</h1>
        <p>{t('science.intro')}</p>
        <ul>
          <li><strong>Exercise:</strong> {t('science.factors.exercise')}</li>
          <li><strong>Alcohol:</strong> {t('science.factors.alcohol')}</li>
          <li><strong>Smoking:</strong> {t('science.factors.smoking')}</li>
          <li><strong>Animal Protein:</strong> {t('science.factors.animalProtein')}</li>
          <li><strong>Unhealthy Diet:</strong> {t('science.factors.unhealthyDiet')}</li>
          <li><strong>BMI:</strong> {t('science.factors.bmi')}</li>
          <li><strong>Sleep:</strong> {t('science.factors.sleep')}</li>
          <li><strong>Stress:</strong> {t('science.factors.stress')}</li>
          <li><strong>Heat Exposure:</strong> {t('science.factors.heatExposure')}</li>
          <li><strong>Drugs:</strong> {t('science.factors.drugs')}</li>
          <li><strong>Toxins:</strong> {t('science.factors.toxins')}</li>
          <li><strong>Medical Issues:</strong> {t('science.factors.medical')}</li>
        </ul>
        <p>{t('science.learnMore')} <a href="https://pubmed.ncbi.nlm.nih.gov" target="_blank" rel="noopener noreferrer">{t('science.pubMed')}</a>.</p>
      </div>
    </div>
  );
};

const LanguageSelector = () => {
  const { i18n } = useTranslation();
  
  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    localStorage.setItem('i18nextLng', lng);
  };
  
  return (
    <div className="language-selector">
      <button onClick={() => changeLanguage('en')} className={i18n.language === 'en' ? 'active' : ''}>EN</button>
      <button onClick={() => changeLanguage('es')} className={i18n.language === 'es' ? 'active' : ''}>ES</button>
      <button onClick={() => changeLanguage('de')} className={i18n.language === 'de' ? 'active' : ''}>DE</button>
      <button onClick={() => changeLanguage('it')} className={i18n.language === 'it' ? 'active' : ''}>IT</button>
    </div>
  );
};

const questionConfig = [
  { id: 'age', type: 'number', min: 18, max: 85 },
  { id: 'bmi', type: 'options' },
  { id: 'exercise', type: 'options' },
  { id: 'sedentary', type: 'options' },
  { id: 'smoking', type: 'options' },
  { id: 'alcohol', type: 'options' },
  { id: 'drugs', type: 'options' },
  { id: 'animalProtein', type: 'options' },
  { id: 'unhealthyDiet', type: 'options' },
  { id: 'fruits', type: 'options' },
  { id: 'sleep', type: 'options' },
  { id: 'stress', type: 'options' },
  { id: 'heatExposure', type: 'options' },
  { id: 'toxins', type: 'options' },
  { id: 'medical', type: 'options' }
];

const MainApp = () => {
  const { t } = useTranslation();
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState(() => {
    const saved = localStorage.getItem('spermCountAnswers');
    return saved ? JSON.parse(saved) : {};
  });
  const [selectedOption, setSelectedOption] = useState(null);
  const [completeAnswers, setCompleteAnswers] = useState(null);
  const [isResults, setIsResults] = useState(false);
  const [showScienceModal, setShowScienceModal] = useState(false);

  // Initialize AdSense ads when results are shown
  useEffect(() => {
    if (isResults) {
      try {
        (window.adsbygoogle = window.adsbygoogle || []).push({});
      } catch (e) {
        console.error('AdSense error:', e);
      }
    }
  }, [isResults]);

  const questions = questionConfig.map(q => {
    if (q.type === 'options') {
      const optionsObj = t(`options.${q.id}`, { returnObjects: true });
      const options = optionsObj ? Object.keys(optionsObj) : [];
      return { 
        ...q, 
        text: t(`questions.${q.id}`),
        options 
      };
    } else {
      return { 
        ...q, 
        text: t(`questions.${q.id}`)
      };
    }
  });

  useEffect(() => {
    if (answers[questionConfig[step]?.id]) {
      setSelectedOption(answers[questionConfig[step].id]);
    } else {
      setSelectedOption(null);
    }
  }, [step, answers]);

  useEffect(() => {
    localStorage.setItem('spermCountAnswers', JSON.stringify(answers));
  }, [answers]);

  const handleOptionSelect = (value) => {
    setSelectedOption(value);
  };

  const handleNext = () => {
    if (selectedOption !== null) {
      setAnswers({ ...answers, [questionConfig[step].id]: selectedOption });
      if (step === questionConfig.length - 1) {
        const updatedAnswers = { ...answers, [questionConfig[step].id]: selectedOption };
        setCompleteAnswers(updatedAnswers);
        setIsResults(true);
      } else {
        setStep(step + 1);
      }
    }
  };

  const handleBack = () => {
    if (step > 0) {
      setStep(step - 1);
    }
  };

  const calculateSpermCount = () => {
    const defaultAnswers = {
      age: 30,
      exercise: 'Moderate',
      sedentary: 'Low',
      alcohol: 'Occasional',
      smoking: 'None',
      animalProtein: 'Moderate',
      unhealthyDiet: 'VeryUnfrequent',
      fruits: 'Moderate',
      bmi: 'Normal',
      sleep: 'Good',
      stress: 'Moderate',
      heatExposure: 'Low',
      drugs: 'None',
      toxins: 'Low',
      medical: 'None',
    };

    const actualAnswers = completeAnswers || answers;
    const finalAnswers = { ...defaultAnswers, ...actualAnswers };
    const age = Number(finalAnswers.age) || 30;

    const losses = [];
    const factorImpacts = [];
    const chartData = [];
    let baseline = age <= 30 ? 75 : age <= 40 ? 60 : age <= 60 ? 50 : 40;
    let sc = baseline;

    const factors = {
      exercise: { 'None': 0.90, 'Light': 0.98, 'Moderate': 1.05, 'Intense': 1.05, 'Overtraining': 0.85 },
      sedentary: { 'Low': 1.0, 'Moderate': 0.95, 'High': 0.88 },
      alcohol: { 'None': 1.0, 'Occasional': 1.0, 'Moderate': 0.90, 'Heavy': 0.75 },
      smoking: { 'None': 1.0, 'Occasional': 0.90, 'Regular': 0.80, 'Heavy': 0.70 },
      animalProtein: { 'VegetarianBalanced': 1.0, 'Low': 1.0, 'Moderate': 1.0, 'High': 0.95 },
      unhealthyDiet: { 'None': 1.0, 'VeryUnfrequent': 1.0, 'Frequent': 0.90, 'Daily': 0.80 },
      fruits: { 'Low': 0.92, 'Moderate': 1.0, 'High': 1.03 },
      bmi: { 'Underweight': 0.90, 'Normal': 1.0, 'Overweight': 0.90, 'Obese': 0.60 },
      sleep: { 'Poor': 0.85, 'Average': 0.95, 'Good': 1.0, 'Excellent': 1.02 },
      stress: { 'Low': 1.0, 'Moderate': 0.95, 'High': 0.85, 'Chronic': 0.80 },
      heatExposure: { 'Low': 1.0, 'Moderate': 0.90, 'High': 0.75 },
      drugs: { 'None': 1.0, 'Marijuana': 0.90, 'Steroids': 0.60, 'Other': 0.80 },
      toxins: { 'Low': 1.0, 'Moderate': 0.90, 'High': 0.60 },
      medical: { 'None': 1.0, 'Varicocele': 0.70, 'Infections': 0.80, 'Hormonal': 0.75 },
    };

    // Calculate factor impacts for bar chart
    const labelMap = {
      exercise: 'Exercise',
      sedentary: 'Sitting Time',
      alcohol: 'Alcohol',
      smoking: 'Smoking',
      animalProtein: 'Protein Intake',
      unhealthyDiet: 'Junk Food',
      fruits: 'Fruits/Vegetables',
      bmi: 'Weight Status',
      sleep: 'Sleep Quality',
      stress: 'Stress Level',
      heatExposure: 'Heat Exposure',
      drugs: 'Drug Use',
      toxins: 'Toxin Exposure',
      medical: 'Medical Issues'
    };
    
    Object.keys(factors).forEach((key) => {
      const factor = factors[key][finalAnswers[key]] || 1.0;
      if (factor < 1.0) {
        // Calculate actual loss in millions
        const lossAmount = Math.round(baseline * (1 - factor));
        chartData.push({
          name: labelMap[key] || key,
          loss: lossAmount,
          percentage: Math.round((1 - factor) * 100)
        });
      }
    });
    
    // Sort by loss amount (highest first)
    chartData.sort((a, b) => b.loss - a.loss);

    let interactionMultiplier = 1.0;
    if (finalAnswers.unhealthyDiet === 'Daily' && finalAnswers.bmi === 'Obese') {
      interactionMultiplier *= 0.95;
      losses.push({ cause: 'unhealthy diet + obesity interaction', amount: Math.round(baseline * (1 - 0.95)) });
    }
    if (finalAnswers.smoking === 'Heavy' && finalAnswers.alcohol === 'Heavy') {
      interactionMultiplier *= 0.95;
      losses.push({ cause: 'smoking + alcohol interaction', amount: Math.round(baseline * (1 - 0.95)) });
    }

    const addLoss = (cause, factor) => {
      if (factor < 1.0) {
        let loss = baseline - (baseline * factor);
        losses.push({ cause, amount: Math.round(loss) });
      }
    };

    // Calculate individual losses
    if (finalAnswers.exercise === 'None' || finalAnswers.exercise === 'Overtraining') {
      addLoss('lack of exercise or overtraining', factors.exercise[finalAnswers.exercise]);
    }
    if (finalAnswers.sedentary !== 'Low') {
      addLoss('sedentary lifestyle', factors.sedentary[finalAnswers.sedentary]);
    }
    if (finalAnswers.alcohol !== 'None' && finalAnswers.alcohol !== 'Occasional') {
      addLoss('alcohol consumption', factors.alcohol[finalAnswers.alcohol]);
    }
    if (finalAnswers.smoking !== 'None') {
      addLoss('smoking', factors.smoking[finalAnswers.smoking]);
    }
    if (finalAnswers.animalProtein === 'High') {
      addLoss('very high animal protein intake', factors.animalProtein[finalAnswers.animalProtein]);
    }
    if (finalAnswers.unhealthyDiet !== 'None' && finalAnswers.unhealthyDiet !== 'VeryUnfrequent') {
      addLoss('unhealthy diet (processed meats, fast food, sugary snacks, soy)', factors.unhealthyDiet[finalAnswers.unhealthyDiet]);
    }
    if (finalAnswers.fruits === 'Low') {
      addLoss('low fruit/vegetable intake', factors.fruits[finalAnswers.fruits]);
    }
    if (finalAnswers.bmi !== 'Normal') {
      addLoss('weight issues', factors.bmi[finalAnswers.bmi]);
    }
    if (finalAnswers.sleep !== 'Good' && finalAnswers.sleep !== 'Excellent') {
      addLoss('poor sleep', factors.sleep[finalAnswers.sleep]);
    }
    if (finalAnswers.stress !== 'Low') {
      addLoss('high stress', factors.stress[finalAnswers.stress]);
    }
    if (finalAnswers.heatExposure !== 'Low') {
      addLoss('heat exposure', factors.heatExposure[finalAnswers.heatExposure]);
    }
    if (finalAnswers.drugs !== 'None') {
      addLoss('drug use', factors.drugs[finalAnswers.drugs]);
    }
    if (finalAnswers.toxins !== 'Low') {
      addLoss('toxin exposure', factors.toxins[finalAnswers.toxins]);
    }
    if (finalAnswers.medical !== 'None') {
      addLoss('medical issues', factors.medical[finalAnswers.medical]);
    }

    Object.keys(factors).forEach((key) => {
      sc *= factors[key][finalAnswers[key]];
    });
    sc *= interactionMultiplier;

    // Generate actionable tips
    const tips = [];
    if (finalAnswers.smoking !== 'None') {
      tips.push('Quit smoking - sperm count can improve within 3 months');
    }
    if (finalAnswers.bmi === 'Obese' || finalAnswers.bmi === 'Overweight') {
      tips.push('Achieve healthy weight through diet and exercise');
    }
    if (finalAnswers.heatExposure !== 'Low') {
      tips.push('Switch to boxers, avoid hot tubs/saunas, keep laptop off lap');
    }
    if (finalAnswers.stress !== 'Low') {
      tips.push('Practice stress management: meditation, yoga, or therapy');
    }
    if (finalAnswers.sleep === 'Poor') {
      tips.push('Aim for 7-9 hours of quality sleep per night');
    }
    if (finalAnswers.fruits === 'Low') {
      tips.push('Increase antioxidant-rich foods: berries, nuts, leafy greens');
    }
    if (finalAnswers.exercise === 'None') {
      tips.push('Start moderate exercise 3-4 times per week');
    }
    if (finalAnswers.alcohol === 'Heavy') {
      tips.push('Reduce alcohol to moderate levels (< 14 drinks/week)');
    }

    return {
      estimated: Math.round(sc),
      estimatedMin: Math.round(sc * 0.9),
      estimatedMax: Math.round(sc * 1.1),
      optimal: baseline,
      losses,
      factorImpacts,
      chartData,
      tips,
      riskLevel: sc / baseline > 0.8 ? 'Low' : sc / baseline > 0.5 ? 'Medium' : 'High'
    };
  };

  const renderSpermArmy = (count, max) => {
    const icons = Math.round((count / max) * 10);
    return (
      <div className="army">
        {Array.from({ length: icons }).map((_, i) => (
          <span key={i} role="img" aria-label="sperm">🏊‍♂️</span>
        ))}
      </div>
    );
  };

  if (isResults) {
    const { estimated, optimal, estimatedMin, estimatedMax, losses, chartData, tips, riskLevel } = calculateSpermCount();

    return (
      <div className="results">
        <LanguageSelector />
        <h1>{t('appTitle')}</h1>
        
        <div className="medical-disclaimer">
          ⚠️ <strong>Medical Disclaimer:</strong> This is not a medical diagnosis or substitute for a lab semen analysis. 
          Results are an educational estimate based on lifestyle factors only. See a doctor or urologist for accurate testing.
        </div>

        <h2>Your Sperm Health Score: {(estimated / optimal * 100).toFixed(0)}%</h2>
        <p style={{fontSize: '1.2em', color: riskLevel === 'Low' ? '#27AE60' : riskLevel === 'Medium' ? '#F39C12' : '#E74C3C'}}>
          Risk Level: <strong>{riskLevel}</strong>
        </p>

        <h2>{t('optimalArmy')} ({optimal}M/mL):</h2>
        {renderSpermArmy(optimal, optimal)}
        <h2>{t('yourArmy')} ({estimated}M/mL):</h2>
        {renderSpermArmy(estimated, optimal)}
        <p>{(estimated / optimal * 100).toFixed(1)}% {t('ofOptimal')} ({t('range')}: {estimatedMin}-{estimatedMax} M/mL)</p>

        {/* Bar Chart - Losses by Factor */}
        {chartData.length > 0 && (
          <div className="radar-chart-container">
            <h3>Sperm Count Loss by Factor (Millions)</h3>
            <ResponsiveContainer width="100%" height={Math.max(300, chartData.length * 50)}>
              <BarChart 
                data={chartData} 
                layout="vertical"
                margin={{ top: 20, right: 30, bottom: 20, left: 120 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                <XAxis 
                  type="number" 
                  stroke="#ECF0F1"
                  tick={{ fill: '#ECF0F1' }}
                  label={{ value: 'Millions Lost', position: 'insideBottom', offset: -10, fill: '#ECF0F1' }}
                />
                <YAxis 
                  type="category" 
                  dataKey="name" 
                  stroke="#ECF0F1"
                  tick={{ fill: '#ECF0F1', fontSize: 12 }}
                  width={110}
                />
                <Tooltip 
                  contentStyle={{ backgroundColor: '#2a5298', border: '1px solid #ECF0F1', borderRadius: '5px' }}
                  labelStyle={{ color: '#ECF0F1' }}
                  itemStyle={{ color: '#E74C3C' }}
                  formatter={(value, name, props) => [`${value}M (${props.payload.percentage}% reduction)`, 'Loss']}
                />
                <Bar dataKey="loss" radius={[0, 8, 8, 0]}>
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill="#E74C3C" />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            <p style={{fontSize: '0.9em', color: '#A9A9A9', marginTop: '10px'}}>
              Shows actual sperm count reduction (in millions) caused by each negative factor
            </p>
          </div>
        )}

        {/* Losses Breakdown */}
        {losses.length > 0 && (
          <div className="factor-breakdown">
            <h3>{t('soldiersLost')}:</h3>
            {losses.map(loss => {
              let lossKey;
              if (loss.cause === "lack of exercise or overtraining") lossKey = "lackOfExercise";
              else if (loss.cause === "sedentary lifestyle") lossKey = "sedentary";
              else if (loss.cause === "alcohol consumption") lossKey = "alcohol";
              else if (loss.cause === "smoking") lossKey = "smoking";
              else if (loss.cause === "very high animal protein intake") lossKey = "highProtein";
              else if (loss.cause === "unhealthy diet (processed meats, fast food, sugary snacks, soy)") lossKey = "unhealthyDiet";
              else if (loss.cause === "low fruit/vegetable intake") lossKey = "lowFruits";
              else if (loss.cause === "weight issues") lossKey = "weightIssues";
              else if (loss.cause === "poor sleep") lossKey = "poorSleep";
              else if (loss.cause === "high stress") lossKey = "highStress";
              else if (loss.cause === "heat exposure") lossKey = "heatExposure";
              else if (loss.cause === "drug use") lossKey = "drugUse";
              else if (loss.cause === "toxin exposure") lossKey = "toxinExposure";
              else if (loss.cause === "medical issues") lossKey = "medicalIssues";
              else if (loss.cause === "smoking + alcohol interaction") lossKey = "smokingAlcohol";
              else if (loss.cause === "unhealthy diet + obesity interaction") lossKey = "dietObesity";
              else lossKey = loss.cause.replace(/\s+/g, '');
              
              return (
                <div key={loss.cause} className="factor-item negative">
                  <strong>{loss.amount}M</strong> - {t(`losses.${lossKey}`, loss.cause)}
                </div>
              );
            })}
          </div>
        )}

        {/* Actionable Tips */}
        {tips.length > 0 && (
          <div className="actionable-tips">
            <h3>🎯 Quick Wins to Improve Your Score</h3>
            <ul>
              {tips.map((tip, index) => (
                <li key={index}>{tip}</li>
              ))}
            </ul>
          </div>
        )}

        <p>{t('badHabitsMessage')}</p>

        <div className="button-group">
          <button onClick={() => {
            const tweetText = `My sperm army is ${estimated}M/mL, ${(estimated / optimal * 100).toFixed(1)}% of optimal! Time to optimize!`;
            const hashtag = 'MySpermCount';
            const encodedText = encodeURIComponent(tweetText);
            const encodedHashtag = encodeURIComponent(`#${hashtag}`);
            window.open(`https://x.com/intent/tweet?text=${encodedText} ${encodedHashtag}`);
          }}>
            {t('shareOnX')}
          </button>
          <button onClick={() => {
            setStep(0);
            setIsResults(false);
          }}>
            {t('tryAgain')}
          </button>
          <button className="cta-button" onClick={() => window.open('https://www.google.com/search?q=at+home+sperm+test+kit')}>
            📦 Get At-Home Test Kit
          </button>
          <button className="support-btn" onClick={() => window.open('https://buymeacoffee.com/jayfredriksson')}>
            {t('supportUs')}
          </button>
        </div>

        {/* Ad Banner */}
        <div className="ad-banner">
          <ins className="adsbygoogle"
               style={{display:'block'}}
               data-ad-client="ca-pub-1255377982654512"
               data-ad-slot="auto"
               data-ad-format="auto"
               data-full-width-responsive="true"></ins>
        </div>

        <div className="info-footer">
          <div className="banner">
            <button onClick={() => setShowScienceModal(true)} style={{background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', padding: 0}}>
              {t('learnCalculation')}
            </button>
          </div>
          <p>
            <a href="https://www.google.com/search?q=sperm+count+test+near+me" target="_blank" rel="noopener noreferrer">
              {t('wantRealNumber')}
            </a>
          </p>
        </div>

        <div className="privacy-notice">
          🔒 Your privacy matters: We don't store or share your answers. All data stays on your device.
        </div>
        
        <ScienceModal isOpen={showScienceModal} onClose={() => setShowScienceModal(false)} />
      </div>
    );
  }

  const question = questions[step];
  const progressPercentage = ((step + 1) / questions.length) * 100;

  return (
    <div className="wizard">
      <LanguageSelector />
      <h1>{t('appTitle')}</h1>
      <h3>{t('tagline')}</h3>
      
      <div className="privacy-notice">
        🔒 We don't store or share your answers
      </div>

      <div className="question">
        <div className="question-icon">{questionIcons[question.id]}</div>
        <h2>{question.text}</h2>
        {question.id === 'animalProtein' && (
          <p className="tooltip">{t('tooltips.animalProtein')}</p>
        )}
        {question.id === 'unhealthyDiet' && (
          <p className="tooltip">{t('tooltips.unhealthyDiet')}</p>
        )}
        {question.type === 'number' ? (
          <input
            type="number"
            min={question.min}
            max={question.max}
            placeholder={t('enterAge')}
            value={selectedOption === null ? '' : selectedOption}
            onChange={(e) => {
              const value = e.target.value === '' ? null : parseInt(e.target.value, 10);
              setSelectedOption(value);
            }}
            onBlur={(e) => {
              const value = parseInt(e.target.value, 10);
              if (isNaN(value) || value < question.min || value > question.max) {
                setSelectedOption(null);
              }
            }}
          />
        ) : (
          <div className="options">
            {question.options.map((opt) => (
              <button
                key={opt}
                className={selectedOption === opt ? 'selected' : ''}
                onClick={() => handleOptionSelect(opt)}
              >
                {t(`options.${question.id}.${opt}`)}
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="progress-bar">
        <div style={{ width: `${progressPercentage}%` }}></div>
      </div>
      <div className="navigation">
        {step > 0 && <button className="back-btn" onClick={handleBack}>{t('back')}</button>}
        <button className="next-btn" onClick={handleNext} disabled={selectedOption === null}>
          {t('next')}
        </button>
      </div>
      <p>{t('question')} {step + 1}/{questions.length}</p>
    </div>
  );
};

const App = () => {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<MainApp />} />
      </Routes>
    </Router>
  );
};

export default App;
